// Módulo Arquivos — tela (01/10/2026, Walber).
// Armário das cópias congeladas dos relatórios do TOVIS. Pastas à esquerda, lista no meio,
// prévia à direita. Desenho aprovado no Claude Design ("TOVIS — Módulo Arquivos").
// - Só LÊ e GUARDA: nunca toca em FAD-01, pendências ou qualquer outro módulo.
// - A cópia fiel (HTML) aparece num quadro isolado (sem scripts) e vira PDF pela janela de
//   impressão do navegador ("Salvar como PDF") — regra do Handoff 2.1: nada de PDF no servidor.
// - Motor: app/routers/arquivos.py · chamadas: src/arquivosApi.ts
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert, Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle,
  FormControl, FormControlLabel, FormLabel, IconButton, InputAdornment, MenuItem, Radio, RadioGroup,
  Snackbar, TextField, Tooltip, Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SearchIcon from '@mui/icons-material/Search';
import FolderOutlinedIcon from '@mui/icons-material/FolderOutlined';
import ScheduleIcon from '@mui/icons-material/Schedule';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutlined';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import DownloadIcon from '@mui/icons-material/Download';
import PrintIcon from '@mui/icons-material/Print';
import SendOutlinedIcon from '@mui/icons-material/SendOutlined';
import RestoreFromTrashIcon from '@mui/icons-material/RestoreFromTrash';
import OpenInFullIcon from '@mui/icons-material/OpenInFull';
import CloseIcon from '@mui/icons-material/Close';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { API_URL } from './config';
import { useAuth } from './AuthContext';
import {
  Arquivo, Detalhe, FiltrosLista, LIMITE_BYTES, PASTAS, PastaId, Resumo, arquivosApi,
  formatarDataHora, formatarDia, hojeISO, infoPasta, mensagemDeErro, nomeParaSalvar, tamanhoLegivel,
} from './arquivosApi';

// ── Cores e fontes (sóbrio; cor forte só na ação principal) ────────────────
const C = {
  tinta: '#17211D', suave: '#5A6661', linha: '#DCE1DC', linhaFina: '#EEF1EE', fundo: '#F4F5F2',
  acento: '#0E5A4A', acentoEscuro: '#0A4236', acentoClaro: '#E3EFEA', perigo: '#A1271B',
};
const MONO = "'JetBrains Mono', ui-monospace, monospace";
const CHAVE_SESSAO = 'tovis-arquivos-filtros';

type Vista = PastaId | 'recentes' | 'lixeira';
type Tom = 'ok' | 'aviso' | 'neutro' | 'auto' | 'lixeira' | 'versao';
const TONS: Record<Tom, { bg: string; cor: string; borda: string }> = {
  ok:      { bg: '#E4F1E8', cor: '#1C5E38', borda: '#BFDCC9' },
  aviso:   { bg: '#FCE9DD', cor: '#8F3A0B', borda: '#F2C8AE' },
  neutro:  { bg: '#EEF0EE', cor: '#47524D', borda: '#D9DED9' },
  auto:    { bg: '#E1F0EE', cor: '#0E5A4A', borda: '#BBDCD5' },
  lixeira: { bg: '#FBE9E7', cor: '#8A2015', borda: '#F1C2BC' },
  versao:  { bg: '#E7EDF6', cor: '#2B4A78', borda: '#C7D4E8' },
};

interface Ciclo { id: number; nome: string; ativo?: boolean }
interface Area { id: number; nome: string }
type Previa = { tipo: 'html'; html: string } | { tipo: 'pdf' | 'imagem'; url: string; mime: string };

const btnSec = {
  minHeight: 44, borderRadius: 2, textTransform: 'none', fontWeight: 600, color: C.tinta,
  borderColor: '#CBD2CC', bgcolor: '#fff', '&:hover': { borderColor: '#AEB8B1', bgcolor: '#F3F6F3' },
} as const;
const btnPri = {
  minHeight: 44, borderRadius: 2, textTransform: 'none', fontWeight: 600, bgcolor: C.acento,
  boxShadow: 'none', '&:hover': { bgcolor: C.acentoEscuro, boxShadow: 'none' },
} as const;

// Listas com opção vazia ("Todos", "Nenhuma"): mostram a opção escolhida dentro da caixa.
const MOSTRAR_VAZIO = { select: { displayEmpty: true }, inputLabel: { shrink: true } } as const;
const SEMANAS = Array.from({ length: 53 }, (_, i) => 53 - i);

/** '' → sem filtro; '4' → 4 */
function num(v: string): number | undefined { return v === '' ? undefined : Number(v); }

function lerSessao(): { vista?: Vista; ciclo?: string; area?: string } {
  try { return JSON.parse(sessionStorage.getItem(CHAVE_SESSAO) || '{}'); } catch { return {}; }
}

function Selo({ tom, children }: { tom: Tom; children: React.ReactNode }) {
  const t = TONS[tom];
  return (
    <Box component="span" sx={{
      display: 'inline-flex', alignItems: 'center', height: 22, px: 1, borderRadius: 999, fontSize: 12,
      fontWeight: 600, whiteSpace: 'nowrap', bgcolor: t.bg, color: t.cor, border: `1px solid ${t.borda}`,
    }}>{children}</Box>
  );
}

function selosDo(a: Arquivo): { tom: Tom; texto: string }[] {
  const s: { tom: Tom; texto: string }[] = [];
  if (a.na_lixeira) s.push({ tom: 'lixeira', texto: 'Na lixeira' });
  if (a.ultimo_envio) s.push({ tom: 'ok', texto: `Enviado · ${formatarDia(a.ultimo_envio.data_envio, false)}` });
  else if (infoPasta(a.pasta).enviavel && a.origem !== 'fora') s.push({ tom: 'neutro', texto: 'Não enviado' });
  if (a.avisos && a.avisos.length) s.push({ tom: 'aviso', texto: a.avisos.length === 1 ? 'Com aviso' : `${a.avisos.length} avisos` });
  if (a.origem === 'automatico') s.push({ tom: 'auto', texto: 'Arquivado sozinho' });
  if (a.origem === 'fora') s.push({ tom: 'neutro', texto: 'De fora' });
  if (!a.atual) s.push({ tom: 'versao', texto: 'Substituído' });
  return s;
}

function IconeFormato({ formato, cor }: { formato: Arquivo['formato']; cor: string }) {
  const rotulo = formato === 'pdf' ? 'PDF' : formato === 'imagem' ? 'IMG' : 'TOVIS';
  return (
    <Box aria-hidden sx={{
      width: 38, height: 46, flexShrink: 0, border: '1px solid #D5DBD6', borderRadius: 1, bgcolor: '#fff',
      display: 'flex', alignItems: 'flex-end', justifyContent: 'center', pb: 0.75, position: 'relative', overflow: 'hidden',
    }}>
      <Box sx={{ position: 'absolute', top: 0, left: 0, right: 0, height: 5, bgcolor: cor }} />
      <Box component="span" sx={{ fontFamily: MONO, fontSize: 9, fontWeight: 600, color: C.suave, letterSpacing: '0.04em' }}>{rotulo}</Box>
    </Box>
  );
}

function tituloDoGrupo(a: Arquivo, vista: Vista): string {
  if (vista === 'recentes') return 'Últimos arquivados';
  if (vista === 'lixeira') return 'Na lixeira';
  const ciclo = a.ciclo_nome ?? (a.referencia ? a.referencia.split(' · ')[0] : 'Sem ciclo');
  return a.area_nome ? `${ciclo} · ${a.area_nome}` : ciclo;
}

/** Prévia lateral: a cópia é desenhada na largura de uma tela de computador e reduzida para caber
 *  no quadro. (Num quadro estreito o relatório "acharia" que está num celular e embaralharia tudo.) */
const LARGURA_VIRTUAL = 1100;
const ALTURA_QUADRO = 440;
function PreviaReduzida({ html, titulo, iframeRef }: { html: string; titulo: string; iframeRef: React.RefObject<HTMLIFrameElement | null> }) {
  const caixa = useRef<HTMLDivElement | null>(null);
  const [escala, setEscala] = useState(0.33);
  useEffect(() => {
    const el = caixa.current;
    if (!el) return;
    const medir = () => setEscala(Math.min(1, Math.max(0.1, el.clientWidth / LARGURA_VIRTUAL)));
    medir();
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(medir) : null;
    ro?.observe(el);
    return () => ro?.disconnect();
  }, []);
  return (
    <Box ref={caixa} sx={{ position: 'relative', width: '100%', height: ALTURA_QUADRO, overflow: 'hidden', bgcolor: '#fff' }}>
      <iframe ref={iframeRef} title={`Prévia: ${titulo}`} sandbox="allow-same-origin allow-modals" srcDoc={html}
        style={{ position: 'absolute', top: 0, left: 0, width: LARGURA_VIRTUAL, height: ALTURA_QUADRO / escala, border: 0,
          background: '#fff', transform: `scale(${escala})`, transformOrigin: 'top left' }} />
    </Box>
  );
}

/** Abre a janela de impressão da cópia fiel. O nome sugerido do PDF vem do título da página. */
function imprimirCopia(el: HTMLIFrameElement | null, nome: string) {
  const janela = el?.contentWindow;
  const doc = el?.contentDocument;
  if (!el || !janela || !doc) return;
  const zoomAntes = doc.documentElement.style.getPropertyValue('zoom');
  const tituloAntes = document.title;
  const tituloCopiaAntes = doc.title;
  doc.documentElement.style.removeProperty('zoom');
  document.title = nome;
  doc.title = nome;
  try {
    janela.focus();
    janela.print();
  } finally {
    document.title = tituloAntes;
    doc.title = tituloCopiaAntes;
    if (zoomAntes) doc.documentElement.style.setProperty('zoom', zoomAntes);
  }
}

export default function Arquivos({ onVoltar }: { onVoltar: () => void }) {
  const { token } = useAuth();
  const sessao = useMemo(lerSessao, []);

  // Filtros e navegação
  const [vista, setVista] = useState<Vista>(sessao.vista ?? 'recentes');
  const [cicloFiltro, setCicloFiltro] = useState<string>(sessao.ciclo ?? '');
  const [areaFiltro, setAreaFiltro] = useState<string>(sessao.area ?? '');
  const [seFiltro, setSeFiltro] = useState<string>('');
  const [busca, setBusca] = useState('');
  const [buscaFinal, setBuscaFinal] = useState('');
  const [ciclos, setCiclos] = useState<Ciclo[]>([]);
  const [areas, setAreas] = useState<Area[]>([]);

  // Dados
  const [resumo, setResumo] = useState<Resumo | null>(null);
  const [itens, setItens] = useState<Arquivo[]>([]);
  const [carregando, setCarregando] = useState(false);
  const [erro, setErro] = useState('');
  const [selId, setSelId] = useState<number | null>(null);
  const [detalhe, setDetalhe] = useState<Detalhe | null>(null);
  const [previa, setPrevia] = useState<Previa | null>(null);
  const [carregandoPrevia, setCarregandoPrevia] = useState(false);
  const [erroPrevia, setErroPrevia] = useState('');
  const [recarga, setRecarga] = useState(0);

  // Diálogos
  const [telaCheia, setTelaCheia] = useState(false);
  const [dlgEnvio, setDlgEnvio] = useState(false);
  const [envio, setEnvio] = useState({ destino: 'Regional (Amargosa)', outro: '', data: hojeISO(), obs: '' });
  const [dlgLixeira, setDlgLixeira] = useState(false);
  const [motivo, setMotivo] = useState('');
  const [dlgFora, setDlgFora] = useState(false);
  const [fora, setFora] = useState<{ arquivo: File | null; titulo: string; pasta: PastaId; ciclo: string; area: string; obs: string }>(
    { arquivo: null, titulo: '', pasta: 'fora', ciclo: '', area: '', obs: '' });
  const [erroDialogo, setErroDialogo] = useState('');
  const [salvando, setSalvando] = useState(false);
  const [aviso, setAviso] = useState<{ texto: string; tom: 'success' | 'error' } | null>(null);

  const iframeRef = useRef<HTMLIFrameElement | null>(null);
  const iframeCheioRef = useRef<HTMLIFrameElement | null>(null);

  // Guarda a navegação na sessão (volta ao mesmo lugar)
  useEffect(() => {
    try { sessionStorage.setItem(CHAVE_SESSAO, JSON.stringify({ vista, ciclo: cicloFiltro, area: areaFiltro })); } catch { /* sem armazenamento */ }
  }, [vista, cicloFiltro, areaFiltro]);

  // Busca espera a pessoa parar de digitar
  useEffect(() => {
    const t = setTimeout(() => setBuscaFinal(busca.trim()), 350);
    return () => clearTimeout(t);
  }, [busca]);

  // Ciclos e áreas para os filtros
  useEffect(() => {
    const headers: Record<string, string> = token ? { Authorization: `Bearer ${token}` } : {};
    fetch(`${API_URL}/api/ciclos/`, { headers }).then(r => (r.ok ? r.json() : [])).then((l: Ciclo[]) => {
      setCiclos([...l].sort((a, b) => a.nome.localeCompare(b.nome, 'pt-BR', { numeric: true })));
    }).catch(() => setCiclos([]));
    fetch(`${API_URL}/api/areas/`, { headers }).then(r => (r.ok ? r.json() : [])).then((l: Area[]) => {
      setAreas([...l].sort((a, b) => a.nome.localeCompare(b.nome, 'pt-BR')));
    }).catch(() => setAreas([]));
  }, [token]);

  const carregarLista = useCallback(async () => {
    setCarregando(true);
    setErro('');
    try {
      const f: FiltrosLista = { ciclo_id: num(cicloFiltro), area_id: num(areaFiltro), se: num(seFiltro), busca: buscaFinal || undefined };
      if (vista === 'lixeira') f.lixeira = true;
      else if (vista === 'recentes') f.limite = 30;
      else f.pasta = vista;
      const [lista, res] = await Promise.all([arquivosApi.listar(token, f), arquivosApi.resumo(token, num(cicloFiltro))]);
      setItens(lista);
      setResumo(res);
      setSelId(atual => (atual !== null && lista.some(a => a.id === atual) ? atual : (lista[0]?.id ?? null)));
    } catch (e) {
      setErro(mensagemDeErro(e));
    } finally {
      setCarregando(false);
    }
  }, [token, vista, cicloFiltro, areaFiltro, seFiltro, buscaFinal]);

  useEffect(() => { carregarLista(); }, [carregarLista]);

  // Prévia e detalhes do documento escolhido
  useEffect(() => {
    if (selId === null) { setDetalhe(null); setPrevia(null); setCarregandoPrevia(false); setErroPrevia(''); return; }
    let vivo = true;
    setCarregandoPrevia(true);
    setErroPrevia('');
    Promise.all([arquivosApi.detalhe(token, selId), arquivosApi.conteudo(token, selId)])
      .then(async ([d, blob]) => {
        if (!vivo) return;
        setDetalhe(d);
        if (d.arquivo.formato === 'html') {
          const html = await blob.text();
          if (vivo) setPrevia({ tipo: 'html', html });
        } else {
          const url = URL.createObjectURL(blob);
          if (vivo) setPrevia({ tipo: d.arquivo.formato, url, mime: blob.type }); else URL.revokeObjectURL(url);
        }
      })
      .catch(e => { if (vivo) { setDetalhe(null); setPrevia(null); setErroPrevia(mensagemDeErro(e)); } })
      .finally(() => { if (vivo) setCarregandoPrevia(false); });
    return () => { vivo = false; };
  }, [selId, token, recarga]);

  // Libera a memória da prévia anterior (PDF/imagem)
  useEffect(() => () => { if (previa && previa.tipo !== 'html') URL.revokeObjectURL(previa.url); }, [previa]);

  const grupos = useMemo(() => {
    const g: { titulo: string; itens: Arquivo[] }[] = [];
    for (const a of itens) {
      const t = tituloDoGrupo(a, vista);
      let alvo = g.find(x => x.titulo === t);
      if (!alvo) { alvo = { titulo: t, itens: [] }; g.push(alvo); }
      alvo.itens.push(a);
    }
    return g;
  }, [itens, vista]);

  const trocarVista = (v: Vista) => { setVista(v); setSelId(null); };
  const recarregarTudo = () => { setRecarga(n => n + 1); carregarLista(); };
  const a = detalhe?.arquivo ?? null;

  // ── Ações ────────────────────────────────────────────────────────────────
  const baixar = () => {
    if (!a || !previa || previa.tipo === 'html') return;
    const ext = previa.tipo === 'pdf' ? 'pdf' : (previa.mime.includes('png') ? 'png' : 'jpg');
    const link = document.createElement('a');
    link.href = previa.url;
    link.download = `${nomeParaSalvar(a)}.${ext}`;
    document.body.appendChild(link);
    link.click();
    link.remove();
  };

  const salvarEmPdf = (el: HTMLIFrameElement | null) => { if (a) imprimirCopia(el, nomeParaSalvar(a)); };

  const abrirEnvio = () => { setEnvio({ destino: 'Regional (Amargosa)', outro: '', data: hojeISO(), obs: '' }); setErroDialogo(''); setDlgEnvio(true); };
  const confirmarEnvio = async () => {
    if (!a) return;
    const destino = envio.destino === 'Outro' ? envio.outro.trim() : envio.destino;
    if (destino.length < 2) { setErroDialogo('Escreva para quem foi enviado.'); return; }
    setSalvando(true);
    try {
      await arquivosApi.registrarEnvio(token, a.id, { destino, data_envio: envio.data, observacao: envio.obs.trim() || undefined });
      setDlgEnvio(false);
      setAviso({ texto: 'Envio registrado.', tom: 'success' });
      recarregarTudo();
    } catch (e) { setErroDialogo(mensagemDeErro(e)); } finally { setSalvando(false); }
  };

  const abrirLixeira = () => { setMotivo(''); setErroDialogo(''); setDlgLixeira(true); };
  const confirmarLixeira = async () => {
    if (!a) return;
    if (motivo.trim().length < 3) { setErroDialogo('Escreva o motivo (pelo menos 3 letras).'); return; }
    setSalvando(true);
    try {
      await arquivosApi.lixeira(token, a.id, motivo.trim());
      setDlgLixeira(false);
      setAviso({ texto: 'Movido para a lixeira. Dá para restaurar quando quiser.', tom: 'success' });
      recarregarTudo();
    } catch (e) { setErroDialogo(mensagemDeErro(e)); } finally { setSalvando(false); }
  };

  const restaurar = async () => {
    if (!a) return;
    try {
      await arquivosApi.restaurar(token, a.id);
      setAviso({ texto: 'Arquivo restaurado.', tom: 'success' });
      recarregarTudo();
    } catch (e) { setAviso({ texto: mensagemDeErro(e), tom: 'error' }); }
  };

  const abrirFora = () => {
    const ativo = ciclos.find(c => c.ativo);
    setFora({ arquivo: null, titulo: '', pasta: 'fora', ciclo: ativo ? String(ativo.id) : '', area: '', obs: '' });
    setErroDialogo('');
    setDlgFora(true);
  };
  const escolherArquivoFora = (f: File | null | undefined) => {
    if (!f) return;
    const ok = /\.(pdf|png|jpe?g)$/i.test(f.name) || ['application/pdf', 'image/png', 'image/jpeg'].includes(f.type);
    if (!ok) { setErroDialogo('Escolha um PDF, PNG ou JPG.'); return; }
    if (f.size > LIMITE_BYTES) { setErroDialogo('Arquivo grande demais (máximo 20 MB).'); return; }
    setErroDialogo('');
    setFora(s => ({ ...s, arquivo: f, titulo: s.titulo || f.name.replace(/\.[^.]+$/, '') }));
  };
  const confirmarFora = async () => {
    if (!fora.arquivo) { setErroDialogo('Escolha o arquivo.'); return; }
    if (fora.titulo.trim().length < 3) { setErroDialogo('Dê um título (pelo menos 3 letras).'); return; }
    setSalvando(true);
    try {
      const id = typeof crypto !== 'undefined' && 'randomUUID' in crypto ? crypto.randomUUID() : `${Date.now()}-${Math.random()}`;
      const novo = await arquivosApi.arquivar(token, {
        arquivo: fora.arquivo, nomeArquivo: fora.arquivo.name, pasta: fora.pasta, tipo: 'documento_fora',
        chave: `fora|${id}`, titulo: fora.titulo.trim(), ciclo_id: num(fora.ciclo) ?? null, area_id: num(fora.area) ?? null,
        origem: 'fora', observacao: fora.obs.trim() || null,
      });
      setDlgFora(false);
      setAviso({ texto: `Guardado em “${infoPasta(novo.pasta).nome}”.`, tom: 'success' });
      setVista(novo.pasta);
      setSelId(novo.id);
      setRecarga(n => n + 1);
    } catch (e) { setErroDialogo(mensagemDeErro(e)); } finally { setSalvando(false); }
  };

  // ── Pedaços da tela ──────────────────────────────────────────────────────
  const itemNav = (id: Vista, nome: string, cor: string, n: number | undefined, icone: React.ReactNode) => {
    const ativo = vista === id;
    return (
      <Box key={id} component="button" type="button" onClick={() => trocarVista(id)} aria-current={ativo ? 'page' : undefined}
        sx={{
          display: 'flex', alignItems: 'center', gap: 1.25, width: '100%', minHeight: 48, px: 1.25, py: 0.75,
          border: 'none', borderRadius: 2, cursor: 'pointer', textAlign: 'left', font: 'inherit', color: C.tinta,
          bgcolor: ativo ? C.acentoClaro : 'transparent', '&:hover': { bgcolor: ativo ? C.acentoClaro : '#F3F6F3' },
          '&:focus-visible': { outline: `2px solid ${C.acento}`, outlineOffset: 1 },
        }}>
        <Box sx={{ width: 32, height: 32, borderRadius: 2, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: `${cor}1A`, color: cor }}>
          {icone}
        </Box>
        <Box component="span" sx={{ flex: 1, minWidth: 0, fontSize: 14, fontWeight: ativo ? 700 : 500, color: ativo ? C.acentoEscuro : C.tinta }}>{nome}</Box>
        {n !== undefined && <Box component="span" sx={{ fontFamily: MONO, fontSize: 12, color: C.suave }}>{n}</Box>}
      </Box>
    );
  };

  const tituloVista = vista === 'recentes' ? 'Recentes' : vista === 'lixeira' ? 'Lixeira' : infoPasta(vista).nome;
  const subtitulo = `${itens.length === 1 ? '1 documento' : `${itens.length} documentos`}`
    + (vista === 'recentes' ? ' · de todas as pastas' : '')
    + (cicloFiltro ? ` · ${ciclos.find(c => String(c.id) === cicloFiltro)?.nome ?? ''}` : '');

  const rotulo = { fontSize: 12, fontWeight: 600, color: C.suave } as const;
  const campo = { '& .MuiOutlinedInput-root': { bgcolor: '#fff', borderRadius: 2, minHeight: 44 } } as const;

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: C.fundo, color: C.tinta }}>
      {/* Cabeçalho */}
      <Box sx={{ bgcolor: '#fff', borderBottom: `1px solid ${C.linha}` }}>
        <Box sx={{ maxWidth: 1440, mx: 'auto', px: { xs: 2, md: 4 }, py: 1.5, display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
          <Tooltip title="Voltar aos módulos">
            <IconButton onClick={onVoltar} aria-label="Voltar aos módulos" sx={{ width: 44, height: 44 }}><ArrowBackIcon /></IconButton>
          </Tooltip>
          <Box sx={{ flex: 1, minWidth: 220 }}>
            <Typography component="h1" sx={{ fontSize: 24, fontWeight: 700, lineHeight: 1.2 }}>Arquivos</Typography>
            <Typography sx={{ fontSize: 13, color: C.suave }}>
              Cópias congeladas dos relatórios. Depois de arquivado, nada muda — refazer vira nova versão.
            </Typography>
          </Box>
          <Button variant="outlined" startIcon={<UploadFileIcon />} onClick={abrirFora} sx={btnSec}>Enviar arquivo de fora</Button>
        </Box>
      </Box>

      <Box sx={{ maxWidth: 1440, mx: 'auto', px: { xs: 2, md: 4 }, py: 3, display: 'flex', flexDirection: 'column', gap: 2 }}>
        {/* Filtros */}
        <Box sx={{ bgcolor: '#fff', border: `1px solid ${C.linha}`, borderRadius: 3, p: 2, display: 'flex', flexWrap: 'wrap', gap: 1.5, alignItems: 'flex-end' }}>
          <TextField label="Buscar" placeholder="Nome, área, SE, mês…" value={busca} onChange={e => setBusca(e.target.value)}
            size="small" sx={{ flex: '1 1 300px', ...campo }}
            slotProps={{ input: { startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> } }} />
          <TextField select label="Ciclo" value={cicloFiltro} onChange={e => setCicloFiltro(e.target.value)} size="small" sx={{ minWidth: 170, ...campo }} slotProps={MOSTRAR_VAZIO}>
            <MenuItem value="">Todos os ciclos</MenuItem>
            {ciclos.map(c => <MenuItem key={c.id} value={String(c.id)}>{c.nome}</MenuItem>)}
          </TextField>
          <TextField select label="Área" value={areaFiltro} onChange={e => setAreaFiltro(e.target.value)} size="small" sx={{ minWidth: 190, ...campo }} slotProps={MOSTRAR_VAZIO}>
            <MenuItem value="">Todas as áreas</MenuItem>
            {areas.map(ar => <MenuItem key={ar.id} value={String(ar.id)}>{ar.nome}</MenuItem>)}
          </TextField>
          <TextField select label="Semana (SE)" value={seFiltro} onChange={e => setSeFiltro(e.target.value)} size="small"
            sx={{ minWidth: 150, ...campo }} slotProps={MOSTRAR_VAZIO}>
            <MenuItem value="">Todas</MenuItem>
            {SEMANAS.map(n => <MenuItem key={n} value={String(n)}>SE {n}</MenuItem>)}
          </TextField>
        </Box>

        {erro && <Alert severity="error" action={<Button color="inherit" onClick={carregarLista}>Tentar de novo</Button>}>{erro}</Alert>}

        <Box sx={{
          display: 'grid', gap: 2.5, alignItems: 'start',
          gridTemplateColumns: { xs: 'minmax(0, 1fr)', md: '240px minmax(0, 1fr)', lg: '264px minmax(0, 1fr) 400px' },
        }}>
          {/* Pastas */}
          <Box component="nav" aria-label="Pastas" sx={{ bgcolor: '#fff', border: `1px solid ${C.linha}`, borderRadius: 3, p: 1, display: 'flex', flexDirection: 'column', gap: 0.25 }}>
            {itemNav('recentes', 'Recentes', C.acento, undefined, <ScheduleIcon sx={{ fontSize: 18 }} />)}
            <Typography sx={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: C.suave, px: 1.25, pt: 1.75, pb: 0.75 }}>Pastas</Typography>
            {PASTAS.map(p => itemNav(p.id, p.nome, p.cor, resumo?.pastas[p.id], <FolderOutlinedIcon sx={{ fontSize: 18 }} />))}
            <Box sx={{ height: '1px', bgcolor: '#E6EAE6', my: 1, mx: 0.75 }} />
            {itemNav('lixeira', 'Lixeira', C.suave, resumo?.lixeira, <DeleteOutlineIcon sx={{ fontSize: 18 }} />)}
          </Box>

          {/* Lista */}
          <Box component="section" aria-label="Documentos" sx={{ bgcolor: '#fff', border: `1px solid ${C.linha}`, borderRadius: 3, minWidth: 0, overflow: 'hidden' }}>
            <Box sx={{ px: 2.5, pt: 2.25, pb: 1.75, display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: 2 }}>
              <Box>
                <Typography component="h2" sx={{ fontSize: 19, fontWeight: 700 }}>{tituloVista}</Typography>
                <Typography sx={{ fontSize: 13, color: C.suave }}>{subtitulo}</Typography>
              </Box>
              {carregando && <CircularProgress size={20} aria-label="Carregando" />}
            </Box>
            {!carregando && itens.length === 0 && (
              <Box sx={{ px: 2.5, py: 5, borderTop: `1px solid ${C.linhaFina}`, textAlign: 'center', color: C.suave }}>
                {vista === 'lixeira' ? 'A lixeira está vazia.' : 'Nada arquivado aqui ainda. Use o botão “Arquivar” dentro dos módulos.'}
              </Box>
            )}
            {grupos.map(g => (
              <Box key={g.titulo}>
                <Box sx={{ px: 2.5, pt: 1.75, pb: 1, bgcolor: '#F8FAF8', borderTop: '1px solid #E6EAE6', display: 'flex', alignItems: 'center', gap: 1.25 }}>
                  <Typography sx={{ fontSize: 12, fontWeight: 700, letterSpacing: '0.04em', textTransform: 'uppercase', color: '#2E3A35' }}>{g.titulo}</Typography>
                  <Typography sx={{ fontFamily: MONO, fontSize: 12, color: C.suave }}>{g.itens.length === 1 ? '1 documento' : `${g.itens.length} documentos`}</Typography>
                </Box>
                {g.itens.map(item => {
                  const sel = item.id === selId;
                  return (
                    <Box key={item.id} component="button" type="button" onClick={() => setSelId(item.id)} aria-pressed={sel}
                      sx={{
                        display: 'flex', alignItems: 'center', gap: 1.75, width: '100%', minHeight: 64, px: 2.5, py: 1.25,
                        border: 'none', borderTop: `1px solid ${C.linhaFina}`, cursor: 'pointer', textAlign: 'left', font: 'inherit', color: C.tinta,
                        bgcolor: sel ? '#EEF5F2' : 'transparent', boxShadow: sel ? 'inset 0 0 0 1px #9CC7B9' : 'none',
                        '&:hover': { bgcolor: sel ? '#EEF5F2' : '#F6F8F6' },
                        '&:focus-visible': { outline: `2px solid ${C.acento}`, outlineOffset: -2 },
                      }}>
                      <IconeFormato formato={item.formato} cor={infoPasta(item.pasta).cor} />
                      <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 0.4 }}>
                        <Box component="span" sx={{ fontWeight: 600, fontSize: 14, overflowWrap: 'anywhere' }}>{item.titulo}</Box>
                        <Box component="span" sx={{ fontFamily: MONO, fontSize: 12, color: C.suave }}>
                          {`v${item.versao} · arquivado ${formatarDataHora(item.criado_em)}`}
                          {vista === 'recentes' || vista === 'lixeira' ? ` · ${infoPasta(item.pasta).nome}` : ''}
                        </Box>
                      </Box>
                      <Box sx={{ display: { xs: 'none', sm: 'flex' }, flexWrap: 'wrap', gap: 0.75, justifyContent: 'flex-end', maxWidth: 260 }}>
                        {selosDo(item).map(s => <Selo key={s.texto} tom={s.tom}>{s.texto}</Selo>)}
                      </Box>
                    </Box>
                  );
                })}
              </Box>
            ))}
          </Box>

          {/* Prévia */}
          <Box component="aside" aria-label="Documento selecionado" sx={{
            bgcolor: '#fff', border: `1px solid ${C.linha}`, borderRadius: 3, p: 2.5, display: 'flex', flexDirection: 'column', gap: 2,
            position: { lg: 'sticky' }, top: 16, gridColumn: { md: '1 / -1', lg: 'auto' }, minWidth: 0,
          }}>
            {selId === null && !carregando && (
              <Typography sx={{ color: C.suave, textAlign: 'center', py: 6 }}>Escolha um documento na lista.</Typography>
            )}
            {carregandoPrevia && <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress aria-label="Abrindo documento" /></Box>}
            {erroPrevia && <Alert severity="error">{erroPrevia}</Alert>}
            {a && previa && !carregandoPrevia && (
              <>
                <Box sx={{ bgcolor: '#ECEFEC', borderRadius: 2.5, p: 1.5, position: 'relative' }}>
                  {previa.tipo === 'html' && <PreviaReduzida html={previa.html} titulo={a.titulo} iframeRef={iframeRef} />}
                  {previa.tipo === 'pdf' && (
                    <iframe title={`Prévia: ${a.titulo}`} src={previa.url} style={{ width: '100%', height: 440, border: 0, background: '#fff', display: 'block' }} />
                  )}
                  {previa.tipo === 'imagem' && (
                    <img src={previa.url} alt={a.titulo} style={{ width: '100%', maxHeight: 440, objectFit: 'contain', display: 'block', background: '#fff' }} />
                  )}
                </Box>
                <Button size="small" startIcon={<OpenInFullIcon />} onClick={() => setTelaCheia(true)}
                  sx={{ alignSelf: 'flex-end', mt: -1, minHeight: 36, textTransform: 'none', fontWeight: 600, color: C.acento }}>
                  Ver em tela cheia
                </Button>

                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                    {selosDo(a).map(s => <Selo key={s.texto} tom={s.tom}>{s.texto}</Selo>)}
                  </Box>
                  <Typography component="h3" sx={{ fontSize: 17, fontWeight: 700, lineHeight: 1.3, overflowWrap: 'anywhere' }}>{a.titulo}</Typography>
                </Box>

                <Box component="dl" sx={{ m: 0, display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: '10px 16px', fontSize: 13 }}>
                  {([
                    ['Pasta', infoPasta(a.pasta).nome], ['Área', a.area_nome ?? '—'], ['Ciclo', a.ciclo_nome ?? '—'], ['Versão', `v${a.versao}${a.atual ? ' (atual)' : ''}`],
                  ] as [string, string][]).map(([k, v]) => (
                    <Box key={k}><Box component="dt" sx={{ color: C.suave, fontSize: 12 }}>{k}</Box><Box component="dd" sx={{ m: 0, fontWeight: 600 }}>{v}</Box></Box>
                  ))}
                  {a.referencia && (
                    <Box sx={{ gridColumn: '1 / -1' }}><Box component="dt" sx={{ color: C.suave, fontSize: 12 }}>Referência</Box><Box component="dd" sx={{ m: 0, fontWeight: 600 }}>{a.referencia}</Box></Box>
                  )}
                  <Box><Box component="dt" sx={{ color: C.suave, fontSize: 12 }}>Arquivado em</Box><Box component="dd" sx={{ m: 0, fontWeight: 600 }}>{formatarDataHora(a.criado_em)}</Box></Box>
                  <Box><Box component="dt" sx={{ color: C.suave, fontSize: 12 }}>Tamanho</Box><Box component="dd" sx={{ m: 0, fontWeight: 600 }}>{tamanhoLegivel(a.tamanho_bytes)}</Box></Box>
                </Box>

                {a.avisos.length > 0 && (
                  <Box sx={{ bgcolor: '#FFF8E8', border: '1px solid #EBD9A8', borderRadius: 2.5, px: 1.75, py: 1.5, display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, fontSize: 12, fontWeight: 700, color: '#6B4700' }}>
                      <WarningAmberIcon sx={{ fontSize: 18 }} /> Avisos gravados junto com o arquivo
                    </Box>
                    {a.avisos.map(t => <Box key={t} component="span" sx={{ fontSize: 13, color: '#4F3A00' }}>• {t}</Box>)}
                  </Box>
                )}

                {a.na_lixeira && (
                  <Alert severity="warning">
                    Na lixeira desde {formatarDataHora(a.lixeira_em)}{a.lixeira_por ? `, por ${a.lixeira_por}` : ''}. Motivo: {a.lixeira_motivo}
                  </Alert>
                )}

                <Box sx={{ border: `1px solid #E1E6E2`, borderRadius: 2.5, px: 1.75, py: 1.5, display: 'flex', flexDirection: 'column', gap: 0.75, fontSize: 13 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, fontWeight: 600 }}><SendOutlinedIcon sx={{ fontSize: 18, color: C.acento }} /> Entrega</Box>
                  {detalhe && detalhe.envios.length === 0 && <Box component="span" sx={{ color: '#3D4843' }}>Nenhum envio registrado.</Box>}
                  {detalhe?.envios.map(ev => (
                    <Box key={ev.id} component="span" sx={{ color: '#3D4843' }}>
                      {ev.destino} · {formatarDia(ev.data_envio)}{ev.observacao ? ` · ${ev.observacao}` : ''}
                    </Box>
                  ))}
                </Box>

                {detalhe && detalhe.versoes.length > 1 && (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                    <Typography sx={{ fontSize: 13, fontWeight: 700 }}>Versões</Typography>
                    {detalhe.versoes.map(v => (
                      <Box key={v.id} component="button" type="button" onClick={() => setSelId(v.id)} disabled={v.id === a.id}
                        sx={{
                          display: 'flex', alignItems: 'center', gap: 1.5, minHeight: 44, px: 1.25, border: `1px solid ${C.linhaFina}`, borderRadius: 2,
                          bgcolor: v.id === a.id ? '#F5FAF7' : '#fff', cursor: v.id === a.id ? 'default' : 'pointer', font: 'inherit', color: C.tinta, textAlign: 'left',
                          '&:hover': { bgcolor: v.id === a.id ? '#F5FAF7' : '#F6F8F6' },
                        }}>
                        <Box component="span" sx={{ fontFamily: MONO, fontWeight: 600, color: v.atual ? C.acento : C.suave, width: 28 }}>v{v.versao}</Box>
                        <Box component="span" sx={{ flex: 1, fontSize: 13 }}>
                          {v.atual ? 'Atual' : `Substituída em ${formatarDataHora(v.substituido_em)}`}
                          <Box component="span" sx={{ display: 'block', fontSize: 12, color: C.suave }}>Arquivada em {formatarDataHora(v.criado_em)}</Box>
                        </Box>
                        {v.id === a.id && <Box component="span" sx={{ fontSize: 12, color: C.suave }}>aberta</Box>}
                      </Box>
                    ))}
                  </Box>
                )}

                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  {previa.tipo === 'html' ? (
                    <>
                      <Button variant="contained" startIcon={<PrintIcon />} onClick={() => salvarEmPdf(iframeRef.current)} sx={btnPri}>Salvar em PDF / Imprimir</Button>
                      <Typography sx={{ fontSize: 12, color: C.suave }}>Na janela que abrir, escolha “Salvar como PDF” para ter o arquivo.</Typography>
                    </>
                  ) : (
                    <Button variant="contained" startIcon={<DownloadIcon />} onClick={baixar} sx={btnPri}>
                      {previa.tipo === 'pdf' ? 'Baixar PDF' : 'Baixar imagem'}
                    </Button>
                  )}
                  {!a.na_lixeira ? (
                    <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 1 }}>
                      <Button variant="outlined" startIcon={<SendOutlinedIcon />} onClick={abrirEnvio} sx={btnSec}>Registrar envio</Button>
                      <Button variant="outlined" startIcon={<DeleteOutlineIcon />} onClick={abrirLixeira}
                        sx={{ ...btnSec, color: C.perigo, '&:hover': { borderColor: C.perigo, bgcolor: '#FDF3F2' } }}>Lixeira</Button>
                    </Box>
                  ) : (
                    <Button variant="outlined" startIcon={<RestoreFromTrashIcon />} onClick={restaurar} sx={btnSec}>Restaurar da lixeira</Button>
                  )}
                </Box>
              </>
            )}
          </Box>
        </Box>
      </Box>

      {/* Tela cheia */}
      <Dialog fullScreen open={telaCheia && !!a && !!previa} onClose={() => setTelaCheia(false)}>
        {a && previa && (
          <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, px: 2, py: 1.25, borderBottom: `1px solid ${C.linha}` }}>
              <Typography sx={{ flex: 1, fontWeight: 700, fontSize: 16 }}>{a.titulo} · v{a.versao}</Typography>
              {previa.tipo === 'html'
                ? <Button variant="contained" startIcon={<PrintIcon />} onClick={() => salvarEmPdf(iframeCheioRef.current)} sx={btnPri}>Salvar em PDF / Imprimir</Button>
                : <Button variant="contained" startIcon={<DownloadIcon />} onClick={baixar} sx={btnPri}>Baixar</Button>}
              <IconButton onClick={() => setTelaCheia(false)} aria-label="Fechar tela cheia" sx={{ width: 44, height: 44 }}><CloseIcon /></IconButton>
            </Box>
            <Box sx={{ flex: 1, bgcolor: '#ECEFEC', p: { xs: 0, md: 2 }, minHeight: 0 }}>
              {previa.tipo === 'html' && (
                <iframe ref={iframeCheioRef} title={`Documento: ${a.titulo}`} sandbox="allow-same-origin allow-modals" srcDoc={previa.html}
                  style={{ width: '100%', height: '100%', border: 0, background: '#fff', display: 'block' }} />
              )}
              {previa.tipo === 'pdf' && <iframe title={`Documento: ${a.titulo}`} src={previa.url} style={{ width: '100%', height: '100%', border: 0, display: 'block' }} />}
              {previa.tipo === 'imagem' && <img src={previa.url} alt={a.titulo} style={{ width: '100%', height: '100%', objectFit: 'contain', display: 'block' }} />}
            </Box>
          </Box>
        )}
      </Dialog>

      {/* Registrar envio */}
      <Dialog open={dlgEnvio} onClose={() => !salvando && setDlgEnvio(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>Registrar envio</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {a && <Typography sx={{ fontSize: 13, color: C.suave }}>{a.titulo} · v{a.versao}</Typography>}
          <FormControl>
            <FormLabel id="arq-destino" sx={rotulo}>Para quem foi</FormLabel>
            <RadioGroup aria-labelledby="arq-destino" value={envio.destino} onChange={e => setEnvio(s => ({ ...s, destino: e.target.value }))}>
              <FormControlLabel value="Regional (Amargosa)" control={<Radio />} label="Regional (Amargosa)" />
              <FormControlLabel value="Coordenação" control={<Radio />} label="Coordenação" />
              <FormControlLabel value="Outro" control={<Radio />} label="Outro" />
            </RadioGroup>
          </FormControl>
          {envio.destino === 'Outro' && (
            <TextField label="Quem recebeu" value={envio.outro} onChange={e => setEnvio(s => ({ ...s, outro: e.target.value }))} sx={campo} />
          )}
          <TextField label="Data do envio" type="date" value={envio.data} onChange={e => setEnvio(s => ({ ...s, data: e.target.value }))}
            sx={campo} slotProps={{ inputLabel: { shrink: true }, htmlInput: { max: hojeISO() } }} />
          <TextField label="Observação (opcional)" value={envio.obs} onChange={e => setEnvio(s => ({ ...s, obs: e.target.value }))}
            placeholder="Ex.: foi no envelope do fechamento" sx={campo} />
          {erroDialogo && <Alert severity="error">{erroDialogo}</Alert>}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={() => setDlgEnvio(false)} disabled={salvando} variant="outlined" sx={btnSec}>Cancelar</Button>
          <Button onClick={confirmarEnvio} disabled={salvando} variant="contained" sx={btnPri}>{salvando ? 'Salvando…' : 'Registrar envio'}</Button>
        </DialogActions>
      </Dialog>

      {/* Lixeira */}
      <Dialog open={dlgLixeira} onClose={() => !salvando && setDlgLixeira(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>Mover para a lixeira?</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {a && <Typography sx={{ fontFamily: MONO, fontSize: 13 }}>{a.titulo} (v{a.versao})</Typography>}
          <TextField label="Por que está tirando este arquivo? (obrigatório)" value={motivo} onChange={e => setMotivo(e.target.value)}
            multiline minRows={2} autoFocus sx={campo} />
          <Typography sx={{ fontSize: 13, color: '#3D4843' }}>Nada se apaga de vez. Dá para restaurar quando quiser.</Typography>
          {erroDialogo && <Alert severity="error">{erroDialogo}</Alert>}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={() => setDlgLixeira(false)} disabled={salvando} variant="outlined" sx={btnSec}>Cancelar</Button>
          <Button onClick={confirmarLixeira} disabled={salvando} variant="contained"
            sx={{ ...btnPri, bgcolor: C.perigo, '&:hover': { bgcolor: '#861F15', boxShadow: 'none' } }}>
            {salvando ? 'Movendo…' : 'Mover para a lixeira'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Enviar arquivo de fora */}
      <Dialog open={dlgFora} onClose={() => !salvando && setDlgFora(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>Enviar arquivo de fora</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Typography sx={{ fontSize: 13, color: C.suave }}>Ofício, papel escaneado, foto de documento. Fica guardado junto com o resto.</Typography>
          <Box component="label"
            onDragOver={e => e.preventDefault()}
            onDrop={e => { e.preventDefault(); escolherArquivoFora(e.dataTransfer.files?.[0]); }}
            sx={{
              border: '1.5px dashed #9FB3AA', borderRadius: 3, bgcolor: '#F6FAF8', p: 3, display: 'flex', flexDirection: 'column',
              alignItems: 'center', gap: 1, textAlign: 'center', cursor: 'pointer', '&:hover': { bgcolor: '#EFF6F2' },
            }}>
            <input type="file" accept="application/pdf,image/png,image/jpeg,.pdf,.png,.jpg,.jpeg" hidden
              onChange={e => escolherArquivoFora(e.target.files?.[0])} />
            <UploadFileIcon sx={{ color: C.acento, fontSize: 30 }} />
            {fora.arquivo ? (
              <>
                <Box component="span" sx={{ fontWeight: 700 }}>{fora.arquivo.name}</Box>
                <Box component="span" sx={{ fontSize: 13, color: C.suave }}>{tamanhoLegivel(fora.arquivo.size)} · clique para trocar</Box>
              </>
            ) : (
              <>
                <Box component="span" sx={{ fontWeight: 700 }}>Arraste o arquivo para cá ou clique para escolher</Box>
                <Box component="span" sx={{ fontSize: 13, color: C.suave }}>PDF, PNG ou JPG · até 20 MB</Box>
              </>
            )}
          </Box>
          <TextField label="Título" value={fora.titulo} onChange={e => setFora(s => ({ ...s, titulo: e.target.value }))} required sx={campo} />
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'minmax(0, 1fr)', sm: 'repeat(3, minmax(0, 1fr))' }, gap: 1.5 }}>
            <TextField select label="Pasta" value={fora.pasta} onChange={e => setFora(s => ({ ...s, pasta: e.target.value as PastaId }))} sx={campo}>
              {PASTAS.map(p => <MenuItem key={p.id} value={p.id}>{p.nome}</MenuItem>)}
            </TextField>
            <TextField select label="Ciclo" value={fora.ciclo} onChange={e => setFora(s => ({ ...s, ciclo: e.target.value }))} sx={campo} slotProps={MOSTRAR_VAZIO}>
              <MenuItem value="">Sem ciclo</MenuItem>
              {ciclos.map(c => <MenuItem key={c.id} value={String(c.id)}>{c.nome}</MenuItem>)}
            </TextField>
            <TextField select label="Área" value={fora.area} onChange={e => setFora(s => ({ ...s, area: e.target.value }))} sx={campo} slotProps={MOSTRAR_VAZIO}>
              <MenuItem value="">Nenhuma</MenuItem>
              {areas.map(ar => <MenuItem key={ar.id} value={String(ar.id)}>{ar.nome}</MenuItem>)}
            </TextField>
          </Box>
          <TextField label="Observação (opcional)" value={fora.obs} onChange={e => setFora(s => ({ ...s, obs: e.target.value }))}
            multiline minRows={2} placeholder="Ex.: recebido por e-mail da Regional" sx={campo} />
          {erroDialogo && <Alert severity="error">{erroDialogo}</Alert>}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={() => setDlgFora(false)} disabled={salvando} variant="outlined" sx={btnSec}>Cancelar</Button>
          <Button onClick={confirmarFora} disabled={salvando} variant="contained" sx={btnPri}>{salvando ? 'Guardando…' : 'Guardar no Arquivos'}</Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={!!aviso} autoHideDuration={5000} onClose={() => setAviso(null)} anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}>
        {aviso ? <Alert severity={aviso.tom} variant="filled" onClose={() => setAviso(null)}>{aviso.texto}</Alert> : undefined}
      </Snackbar>
    </Box>
  );
}
